import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Wrench, Zap, Package, Cog, Clock, MapPin, ChevronDown } from 'lucide-react';
import Breadcrumb from '../../components/Breadcrumb/Breadcrumb';
import ServiceCard from '../../components/ServiceCard/ServiceCard';
import serving1 from '../../images/Workshop/Workshop1.jpeg';
import serving2 from '../../images/Workshop/Workshop2.jpeg';
import serving3 from '../../images/Workshop/Workshop3.jpeg';
import servicesHeroImage from '../../images/herosection/valveservices.jpg';
import './Services.css';

const Services = () => {
  const [openFaq, setOpenFaq] = React.useState(null);

  const solutions = [
    {
      icon: <Shield className="w-8 h-8" />,
      title: 'Safety Valve and Breather Valves',
      description: 'Comprehensive repair and maintenance for all brands and sizes of safety valves and breather valves.',
      features: [
        'All brands and sizes supported',
        'Shop & field repair services',
        'High and low pressure valves',
        '24/7 technical support',
        'Extensive manufacturer specifications library',
        'Online pressure setting',
        'Spare supply',
        'Reconditioning/refurbishment work'
      ]
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'Control & Isolation Valves Servicing & Refurbishment',
      description: 'Complete valve repair services ranging from 150# to 4500# pressure class for control and isolation valves.',
      features: [
        '150# - 4500# pressure class support',
        'All brands and sizes supported',
        'Workshop refurbishment services',
        'Reconditioning/refurbishment work',
        'Seat, Plug, Disc, Wedge rebuilding',
        'Comprehensive spare parts support',
        'Pan India service network'
      ]
    },
    {
      icon: <Wrench className="w-8 h-8" />,
      title: 'ARC Valve Service',
      description: 'Specialized ARC valve support, maintenance and refurbishment for all makes and models.',
      features: [
        'All brands and sizes supported',
        'ARC valve diagnostics and testing',
        'Component replacement and refurbishment',
        'Reconditioning/refurbishment work',
        'Performance optimization',
        'Emergency repair services',
        'Workshop and on-site support'
      ]
    },
    {
      icon: <Zap className="w-8 h-8" />,
      title: 'Electric & Pneumatic Actuator Maintenance',
      description: 'Electric and pneumatic actuator maintenance with genuine spare parts.',
      features: [
        'Pneumatic Actuators',
        'Electric Actuators',
        'Smart positioners',
        'Troubleshooting',
        'Limit setting',
        'Routine inspections',
        'Diagnostics and preventive maintenance',
        'Reconditioning/refurbishment work',
        'Genuine spare parts inventory'
      ]
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: 'Pump Services',
      description: 'Pump Services and maintenance for optimal performance.',
      features: [
        'Preventive maintenance',
        'Breakdown maintenance',
        'Shutdown maintenance',
        'Spare parts support',
        'Technical consultation',
        'Reconditioning/refurbishment work'
      ]
    },
    {
      icon: <Package className="w-8 h-8" />,
      title: 'Genuine Spares Supply',
      description: 'Comprehensive spare parts support with genuine components across India.',
      features: [
        'SW gaskets',
        'Graphite gland packing',
        'Pressure seal rings',
        'Gland ropes of various sizes',
        'Pan India availability',
        'Quality assurance',
        'Reconditioning/refurbishment work'
      ]
    },
    {
      icon: <Cog className="w-8 h-8" />,
      title: 'Workshop Support Services',
      description: 'State-of-the-art workshop with advanced machining and repair for valve refurbishment, spare manufacturing, and shutdown support.',
      features: [
        'Complete valve overhauling and refurbishment',
        'Precision machining and spare parts manufacturing',
        'Shutdown & emergency support',
        'Quality assurance and compliance'
      ]
    }
  ];

  const faqs = [
    {
      question: 'What brands of valves do you service?',
      answer: 'We service all major brands. Our extensive library of manufacturer specifications ensures proper repair and maintenance for safety valves, breather valves, and other critical equipment.'
    },
    {
      question: 'Do you provide emergency services?',
      answer: 'Yes, we offer 24/7 emergency support for critical system failures. Our certified technicians are available round-the-clock to handle urgent repairs and minimize downtime.'
    },
    {
      question: 'What pressure classes do you support?',
      answer: 'We support valve repairs ranging from 150# to 4500# pressure class, covering both low and high-pressure applications across various industries.'
    },
    {
      question: 'Are your spare parts genuine?',
      answer: 'Absolutely. We maintain an extensive inventory of genuine spare parts for all major brands. This ensures optimal performance, longevity, and warranty compliance.'
    },
    {
      question: 'Do you provide on-site services?',
      answer: 'Yes, we provide both shop and field repair services. Our mobile teams can handle on-site repairs, maintenance, and emergency services at your facility.'
    },
    {
      question: 'What is your service coverage area?',
      answer: 'We provide services across India with a network of certified technicians and service centers. Our pan-India presence ensures quick response times and local support.'
    }
  ];

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="services-page">
      {/* Hero Section */}
      <section 
        className="services-hero"
        style={{
          backgroundImage: `url(${servicesHeroImage})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        <div className="services-hero-overlay"></div>
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="services-hero-content"
          >
            <Breadcrumb />
            <h1 className="services-hero-title">
              Our <span className="services-hero-title-highlight">Services</span>
            </h1>
            <p className="services-hero-description">
              Comprehensive industrial flow control solutions with 24/7 support across India. 
              We ensure your operations run smoothly with expert maintenance and genuine parts.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="services-solutions">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="services-solutions-header"
          >
            {/* <h2 className="services-solutions-title">Our Services</h2>
            <p className="services-solutions-description">
              Comprehensive solutions for all your industrial flow control needs:
            </p> */}
          </motion.div>
          
          <div className="services-solutions-grid">
            {solutions.map((solution, index) => (
              <ServiceCard key={index} service={solution} index={index} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="services-why-choose">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="services-why-choose-header"
          >
            <h2 className="services-why-choose-title">Why Choose Our Services?</h2>
            <p className="services-why-choose-description">
              We are not just a service provider but a reliable partner
            </p>
          </motion.div>
          
          <div className="services-why-choose-grid">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="services-why-choose-content"
            >
              <div className="services-why-choose-item">
                <div className="services-why-choose-icon">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="services-why-choose-item-title">Certified Technicians</h3>
                  <p className="services-why-choose-item-description">
                    Factory-trained experts with years of industry experience
                  </p>
                </div>
              </div>
              
              <div className="services-why-choose-item">
                <div className="services-why-choose-icon">
                  <Clock className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="services-why-choose-item-title">24/7 Support</h3>
                  <p className="services-why-choose-item-description">
                    Round-the-clock technical assistance for critical operations
                  </p>
                </div>
              </div>
              
              <div className="services-why-choose-item">
                <div className="services-why-choose-icon">
                  <Package className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="services-why-choose-item-title">Genuine Parts</h3>
                  <p className="services-why-choose-item-description">
                    Extensive inventory of genuine spare parts for all brands
                  </p>
                </div>
              </div>
              
              <div className="services-why-choose-item">
                <div className="services-why-choose-icon">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="services-why-choose-item-title">Pan India Presence</h3>
                  <p className="services-why-choose-item-description">
                    Service network covering the entire country
                  </p>
                </div>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="services-why-choose-visual"
            >
              <div className="services-why-choose-images">
                {/* Main Image */}
                <div className="services-why-choose-main-image">
                  <img src={serving1} alt="Professional Service" className="services-why-choose-img" />
                  <div className="services-why-choose-overlay">
                    <h4 className="services-why-choose-caption">Professional Service</h4>
                  </div>
                </div>
                
                {/* Side Images */}
                <div className="services-why-choose-side-images">
                  <div className="services-why-choose-small-image">
                    <img src={serving2} alt="Technical Expertise" className="services-why-choose-img" />
                    <div className="services-why-choose-overlay">
                      <h4 className="services-why-choose-caption">Technical Expertise</h4>
                    </div>
                  </div>
                  
                  <div className="services-why-choose-small-image">
                    <img src={serving3} alt="Quality Service" className="services-why-choose-img" />
                    <div className="services-why-choose-overlay">
                      <h4 className="services-why-choose-caption">Quality Service</h4>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="services-faq">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="services-faq-header"
          >
            <h2 className="services-faq-title">Frequently Asked Questions</h2>
            <p className="services-faq-description">
              Get answers to common questions about our services
            </p>
          </motion.div>
          
          <div className="services-faq-container">
            <div className="services-faq-column services-faq-left">
              {faqs.slice(0, 3).map((faq, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="services-faq-card services-faq-card-left"
                >
                  <div className="services-faq-card-number">{index + 1}</div>
                  <div className="services-faq-card-content">
                    <button
                      onClick={() => toggleFaq(index)}
                      className="services-faq-card-question"
                    >
                      <span>{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 transition-transform ${openFaq === index ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === index && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="services-faq-card-answer"
                      >
                        <p>{faq.answer}</p>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <div className="services-faq-column services-faq-right">
              {faqs.slice(3, 6).map((faq, index) => (
                <motion.div
                  key={index + 3}
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  className="services-faq-card services-faq-card-right"
                >
                  <div className="services-faq-card-number">{index + 4}</div>
                  <div className="services-faq-card-content">
                    <button
                      onClick={() => toggleFaq(index + 3)}
                      className="services-faq-card-question"
                    >
                      <span>{faq.question}</span>
                      <ChevronDown className={`w-5 h-5 transition-transform ${openFaq === index + 3 ? 'rotate-180' : ''}`} />
                    </button>
                    {openFaq === index + 3 && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="services-faq-card-answer"
                      >
                        <p>{faq.answer}</p>
                      </motion.div>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
